import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api, errorMessage } from "../lib/api";
import { toast } from "sonner";

const TOPICS = [
    { value: "general", label: "General question" },
    { value: "members", label: "Member account" },
    { value: "advertisers", label: "Advertiser inquiry" },
    { value: "payments", label: "Payments & cashout" },
    { value: "privacy", label: "Privacy / data request" },
    { value: "press", label: "Press / partnerships" },
    { value: "other", label: "Other" },
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        topic: "general",
        message: "",
        website: "", // honeypot
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.message.trim().length < 10) {
            toast.error("Please add at least 10 characters of detail.");
            return;
        }
        setBusy(true);
        try {
            const topicLabel = TOPICS.find((t) => t.value === form.topic)?.label || form.topic;
            await api.post("/contact", {
                name: form.name.trim(),
                email: form.email.trim(),
                subject: `[${topicLabel}] ${form.message.slice(0, 80)}`,
                message: form.message.trim(),
                website: form.website,  // honeypot — should always be empty for real users
            });
            setDone(true);
        } catch (err) {
            toast.error(errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <section className="max-w-2xl mx-auto px-6 py-16" data-testid="contact-page">
                <p className="eyebrow mb-3">CONTACT</p>
                <h1 className="h1 mb-3">Get in touch</h1>
                <p className="text-ink-600 mb-10 leading-relaxed">
                    Got a question or feedback? Send us a note and we'll get back to you as soon as we can. For account-specific or sensitive issues, please log in first so we can locate your records faster.
                </p>

                {done ? (
                    <div className="card-base p-8 text-center" data-testid="contact-success">
                        <h2 className="text-xl font-semibold mb-2">Thanks — we got it.</h2>
                        <p className="text-ink-600 leading-relaxed mb-6">
                            We typically reply within one business day. While you wait, you might find what you're after in the{" "}
                            <Link to="/help" className="underline" data-testid="contact-success-help-link">help center</Link>.
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setDone(false);
                                setForm({ name: "", email: "", topic: "general", message: "", website: "" });
                            }}
                            className="btn btn-ghost border border-ink"
                            data-testid="contact-send-another"
                        >
                            Send another message
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="card-base p-6 space-y-4" data-testid="contact-form" noValidate>
                        <Field label="Your name" required>
                            <input
                                type="text"
                                required
                                className="input-base w-full"
                                value={form.name}
                                onChange={update("name")}
                                data-testid="contact-name"
                                autoComplete="name"
                            />
                        </Field>
                        <Field label="Email address" required>
                            <input
                                type="email"
                                required
                                className="input-base w-full"
                                value={form.email}
                                onChange={update("email")}
                                data-testid="contact-email"
                                autoComplete="email"
                            />
                        </Field>
                        <Field label="What's it about?" required>
                            <select
                                required
                                className="input-base w-full"
                                value={form.topic}
                                onChange={update("topic")}
                                data-testid="contact-topic"
                            >
                                {TOPICS.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Your message" required>
                            <textarea
                                required
                                rows={6}
                                className="input-base w-full"
                                value={form.message}
                                onChange={update("message")}
                                placeholder="The more detail, the faster we can help."
                                data-testid="contact-message"
                                maxLength={5000}
                            />
                        </Field>

                        {/* Honeypot — visually hidden but parseable to bots. */}
                        <div className="absolute -left-[10000px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                            <label>
                                Website (leave blank)
                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={form.website}
                                    onChange={update("website")}
                                />
                            </label>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <p className="font-mono text-[10px] text-ink-600">
                                We never share your email.
                            </p>
                            <button
                                type="submit"
                                disabled={busy}
                                className="btn btn-primary"
                                data-testid="contact-submit"
                            >
                                {busy ? "Sending…" : "Send message"}
                            </button>
                        </div>
                    </form>
                )}
            </section>
            <Footer />
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-ink-600 mb-1 block">
                {label} {required && <span className="text-error">*</span>}
            </span>
            {children}
        </label>
    );
}
